<script>
    import { environment } from "@app/store/envStore.svelte.js";
    import { showModal } from "@app/store/modalStore.svelte.js";
    import { eventGroupState, resetEventGroupState } from "@app/store/eventGroupStore.svelte.js";
    import { projectState, resetProjectState } from "@app/store/projectStore.svelte.js";
    import { authState, resetAuth } from "@app/store/authStore.svelte.js";
    import { metadataState } from "@app/store/metadataStore.svelte.js";
    import { speakerState } from "@app/store/speakerStore.svelte.js";
    import { issueState } from "@app/store/issueStore.svelte.js";
    import { resetMarkerState } from "@app/store/markerStore.svelte.js";
    import { resetHistory } from "@app/store/historyStore.svelte.js";
    import { tick } from "svelte";
    import firebase from "@app/configs/firebase.js";
    import { replace } from "svelte-spa-router";
    /* Firebase */
    import db from "@app/configs/firestore.js";
    /* CC LIB */
    import { saveAs } from "file-saver";
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import * as m from '@app/paraglide/messages.js';
    import {
        KEYBOARD_SHORTCUTS_STORAGE_KEY,
        dispatchKeyboardShortcutsUpdated,
        getStoredKeyboardShortcuts,
    } from "@app/lib/keyboardShortcuts.js";

    /** @type {{currentState: boolean, toggleMenu: (menu: string) => void}} */
    let { currentState, toggleMenu } = $props();

    let updateAvailable = $state(false);

    if (environment.online) {
        db.collection("system")
            .doc("creator")
            .get()
            .then((doc) => {
                if (doc.exists) {
                    let data = doc.data();
                    updateAvailable = data.version !== environment.version;
                }
            });
    }

    function handleToggleMenu() {
        toggleMenu("help");
    }

    function backupUserSettings() {
        console.log("Backing up User Settings!");
        // Define the settings keys we want to export
        const settingsKeys = [
            "cc-editor-defaults",
            "cc-ad-defaults-new",
            "cc-event-group-defaults",
            "cc-shortcuts",
            "cc-theme",
            "cc-project-defaults",
            "cc-style-preset",
            "cc-timing-and-sync-settings",
        ];

        try {
            // Create an object to store all settings
            const settings = {};

            // Iterate through each key and get its value from localStorage
            settingsKeys.forEach((key) => {
                const value = localStorage.getItem(key);
                if (value) {
                    try {
                        // Try to parse the value as JSON
                        settings[key] = JSON.parse(value);
                    } catch (e) {
                        // If parsing fails, store as string
                        settings[key] = value;
                    }
                }
            });

            // Create metadata for the export
            const exportData = {
                version: "1.0",
                timestamp: new Date().toISOString(),
                settings: settings,
            };

            // Convert settings object to a JSON string with pretty printing
            const jsonString = JSON.stringify(exportData, null, 2);

            // Create a Blob with the JSON data
            const blob = new Blob([jsonString], { type: "application/json" });

            // Generate filename with timestamp
            const filename = `user-settings-${new Date().toISOString().split("T")[0]}.json`;

            // Save the file
            saveAs(blob, filename);
        } catch (error) {
            console.error("Error exporting settings:", error);
            throw new Error("Failed to export settings");
        }
    }

    const validateImportedSettings = (data) => {
        // Check if the data has the expected structure
        if (!data || typeof data !== "object") return false;
        if (!data.version || !data.timestamp || !data.settings) return false;

        return true;
    };

    async function importUserSettings() {
        try {
            // Show file input dialog
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                title: m.import_settings(),
                html: `
        <div class="mb-3">
          <label for="settings-file-input" class="form-label">
            ${m.choose_settings_file()}
          </label>
          <input type="file"
                 class="form-control"
                 accept=".json" 
                 id="settings-file-input">
        </div>
      `,
                showCancelButton: true,
                confirmButtonText: m.import_btn(),
                cancelButtonText: m.cancel(),
                didOpen: () => {
                    // Initialize Bootstrap file input
                    const input = document.getElementById(
                        "settings-file-input",
                    );
                    input.addEventListener("change", () => {
                        // Bootstrap's form-control class handles the file name display automatically
                    });
                },
                preConfirm: () => {
                    const fileInput = document.getElementById(
                        "settings-file-input",
                    );
                    if (!fileInput.files.length) {
                        Swal.showValidationMessage(m.please_select_file());
                        return false;
                    }
                    return fileInput.files[0];
                },
            });

            if (!result.isConfirmed || !result.value) {
                return;
            }

            // Read the file
            const file = result.value;
            const fileContent = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = (e) => reject(e);
                reader.readAsText(file);
            });

            // Parse and validate the JSON
            const settingsData = JSON.parse(fileContent);

            if (!validateImportedSettings(settingsData)) {
                throw new Error("Invalid settings file format");
            }

            // Show confirmation dialog
            const confirmResult = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                title: m.confirm_import(),
                text: m.overwrite_settings_confirm(),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: m.yes_import_settings(),
                cancelButtonText: m.cancel(),
            });

            if (!confirmResult.isConfirmed) {
                return;
            }

            // Import the settings
            Object.entries(settingsData.settings).forEach(([key, value]) => {
                console.log(JSON.stringify(value));
                //Check if value is already a string
                if (typeof value === "string") {
                    localStorage.setItem(key, value);
                } else {
                    localStorage.setItem(key, JSON.stringify(value));
                }                
            });

            if (Object.prototype.hasOwnProperty.call(settingsData.settings, KEYBOARD_SHORTCUTS_STORAGE_KEY)) {
                dispatchKeyboardShortcutsUpdated(
                    getStoredKeyboardShortcuts(environment.mac),
                );
            }

            // Show success message
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.success(),
                text: m.settings_imported_success(),
                icon: "success",
            });
        } catch (error) {
            console.error("Error importing settings:", error);

            // Show error message
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.error(),
                text: error.message || m.failed_import_settings(),
                icon: "error",
            });
        }
    }

    async function resetApplication() {
        /* Reset Stores */
        if (eventGroupState[projectState.selected]) {
            eventGroupState[projectState.selected].selected = [];
            await tick();
        }

        projectState.selected = false;
        await tick();
        resetEventGroupState();
        await tick();
        resetProjectState();
        metadataState.length = 0;
        speakerState.length = 0;
        issueState.length = 0;
        resetMarkerState();
        resetAuth();
        resetHistory();
        localStorage.clear();

        if (environment.online) {
            firebase.auth().signOut();
        }

        if (environment.electron) {
            const ipcRenderer = window.ipcRenderer;
            ipcRenderer.invoke("quit");
        } else {
            replace("/login");
        }
    }
</script>

<li class="nav-item dropdown {currentState ? 'show' : ''}" role="menuitem">
    <a onclick={handleToggleMenu} 
       class="dropdown-toggle nav-link" 
       href="#!/"
       id="helpMenuButton"
       aria-haspopup="true"
       aria-expanded={currentState}
       role="button"
       aria-label={m.help_menu()}>{m.help()}</a>
    <div class="dropdown-menu {currentState ? 'show' : ''}" 
         role="menu" 
         aria-labelledby="helpMenuButton">
        <!-- Team Management -->
        {#if (authState.plan === "pro" || authState.plan === "enterprise") && environment.online && authState?.user?.role === "admin"}
            <a class="dropdown-item" href="#!/" 
               onclick={() => showModal("teamManager")}
               id="teamManagement"
               role="menuitem"
               aria-label={m.open_team_management()}>
                <i class="bi bi-people-fill" aria-hidden="true"></i> {m.team_management()}
            </a>
            <div class="dropdown-divider"></div>
        {/if}
        {#if environment.online}
            <!-- Subscription Management -->
            {#if authState?.user?.role === "admin"}
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => showModal("subscriptionManagement")}
                >{m.manage_subscription()}</a
            >
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => showModal("usage")}
                >{m.reporting_usage()}</a
            >
            {/if}
            <!-- User Guide -->
            <a
                class="dropdown-item"
                href="https://www.closedcaptioncreator.com/docs"
                target="_blank"
                rel="noreferrer"
                >{m.online_user_guide()} <i class="bi bi-box-arrow-up-right small"
                ></i></a
            >
            <a
                class="dropdown-item"
                href="https://www.closedcaptioncreator.com/support.html"
                target="_blank"
                rel="noreferrer"
            >
                {m.contact_support()} <i class="bi bi-box-arrow-up-right small"></i>
            </a>
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => showModal("offlineActivation")}
                >{m.offline_activation()}</a
            >            
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => showModal("autoSave")}
                >{m.restore_event_groups()}</a
            >
            <div class="dropdown-divider"></div>
        {/if}
        <a onclick={backupUserSettings} 
           class="dropdown-item" 
           href="#!/"
           id="backupSettings"
           role="menuitem"
           aria-label={m.backup_user_settings_desc()}>
            <i class="bi bi-download" aria-hidden="true"></i> {m.backup_user_settings()}
        </a>
        <a onclick={importUserSettings} class="dropdown-item" href="#!/"
            >{m.import_user_settings()}</a
        >
        <a onclick={resetApplication} class="dropdown-item" href="#!/"
            >{m.reset_application()}</a
        >
        <hr class="dropdown-divider" />
        <a
            class="dropdown-item"
            href="#!/"
            onclick={() => showModal("tips")}>{m.whats_new()}</a
        >
        <a
            class="dropdown-item"
            href="#!/"
            onclick={() => showModal("about")}>{m.about_cc_creator()}</a
        >
        {#if updateAvailable}
            <a
                class="dropdown-item text-warning"
                href="https://www.closedcaptioncreator.com/downloads.html"
                target="_blank"
                title={m.update_available()}
                ><i class="bi bi-stars"></i> {m.update_available()}</a
            >
        {:else if environment.online}
            <a class="dropdown-item disabled" href="#!/">{m.no_updates_available()}</a
            >
        {/if}
    </div>
</li>
